import { useGet } from '@/query/fetchHooks';
import { useParams } from 'react-router-dom';
import { useState, useContext, useEffect } from 'react';
import {
  PageHeader,
  Button,
  PersonAvatar,
  Checkbox,
  InputText,
  IconButton,
} from '@teo/components';
import { ChevronRight, Search, Download } from '@teo/components/icons';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { useMobile, nthLastIndexOf, parseFilter, downloadLink } from '@/util';
import { Userwithorganisationwithidps } from '@/models';
import MobileSubPageMenu from '@/components/HeaderMobile/MobileSubPageMenu';
import AssignProfiles from '@/components/Competencies/Profiles/AssignProfiles';
import { TeamProfessionCompetenceMatrix } from './TeamProfessionCompetenceMatrix';
import { BreadCrumbsDetails } from '@/components/BreadCrumbsDetails';
import { NavContext } from '@/components/NavContext';
import ComponentDeleteModal from '@/components/DeleteModal/ComponentDeleteModal';
import { axios } from '@/axios';
import { config } from '@/config';
import { NotificationContext } from '@/components/Contexts/NotificationContext';
import EmptyTable from '@/components/DataTable/EmptyTable';

function FollowUpTeamCompetencies() {
  const { teamId, organisationId } = useParams();
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { setSubPage } = useContext(NavContext);
  const { setShowNotification } = useContext(NotificationContext);

  const [teamInfo, setTeamInfo] = useState<any>(null);

  const [expandedProfileIds, setExpandedProfileIds] = useState<
    Set<string | number>
  >(new Set<string | number>());

  const [isAssignProfilesOpen, setIsAssignProfilesOpen] = useState(false);

  const { auth } = useContext(AuthContext);
  const authUser = auth?.user as Userwithorganisationwithidps;
  const canAssignProfiles =
    Math.max(authUser?.role || 0, authUser?.activeOrganisationRole || 0) >= 50;

  const [selectedRow, setSelectedRow] = useState<Set<string | number>>(
    new Set<string | number>()
  );
  const [selectMode, setSelectMode] = useState(true);

  const [isOpenDelModal, setIsOpenDelModal] = useState(false);
  const [profileInpFilter, setProfileInpFilter] = useState('');

  useEffect(() => {
    setSubPage(true);
    return () => setSubPage(false);
  }, []);

  useGet(`/team/${authUser?.activeOrganisationId}/${teamId}`, {
    onSuccess: (result: any) => {
      setTeamInfo(result?.data || {});
    },
  });

  const teamProfilesResult: any = useGet('/teamprofessiondetails', {
    teamId,
    organisationId,
    count: 1000,
    sort: 'professionName',
    q: parseFilter(profileInpFilter),
    enabled: !!teamId && !!organisationId,
  });
  const teamProfiles = teamProfilesResult?.data?.data || [];

  const basePath = window.location.pathname.slice(
    0,
    nthLastIndexOf(window.location.pathname, '/', 2)
  );

  const handleItemSelect = (key: any) => {
    const newSelected = new Set(selectedRow);
    if (
      (newSelected.has(key) && selectMode) ||
      (!newSelected.has(key) && !selectMode)
    ) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedRow(newSelected);
  };

  const deleteCompetencyProfile = () => {
    [...selectedRow]?.map((professionId: any) => {
      axios
        .delete(`${config.backend}/teamprofession/${teamId}/${professionId}`)
        .then(() => {
          setSelectedRow(new Set<string | number>());
          teamProfilesResult.refetch();
          setIsOpenDelModal(false);
        });
    });
  };

  const downloadReport = async (professionId: any) => {
    try {
      const result: any = await axios.post(`${config.teoreport}/report`, {
        download: true,
        lang: authUser?.lang,
        organisationId: organisationId,
        formType: 'competenceMatrixXlsx',
        professionIds: [professionId],
        teamIds: [teamId],
      });

      if (result?.data?.link) {
        downloadLink(result.data.link);
      }
      setShowNotification({
        message: t('pages.report_page.report_download_message'),
        delay: 5000,
      });
    } catch (error: any) {
      console.error('Report request failed:', error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('pages.report.report_error');

      setShowNotification({
        message: errorMessage,
        delay: 5000,
      });
    }
  };

  return (
    <div className="max-w-6xl flex-col pb-8">
      {isMobile && <MobileSubPageMenu to={-1} />}
      <BreadCrumbsDetails
        prevPathname={basePath + '?tab=teams'}
        prevName={t('breadcrumbs.TEAMS')}
        pathname={window.location.pathname}
        name={teamInfo?.teamName}
      />
      <div className="pb-2 md:pt-6">
        {isMobile ? (
          <div className="flex w-full flex-col gap-4">
            <div className="flex items-center">
              <PersonAvatar
                inline
                id={teamInfo?.id}
                name={teamInfo?.teamName}
                bgColor={teamInfo?.color ? teamInfo?.color : null}
              ></PersonAvatar>
              <h1 className="break-all text-lg font-semibold text-black sm:text-xl">
                {teamInfo?.teamName}
              </h1>
            </div>

            <div className="flex w-full flex-row items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3">
                {canAssignProfiles && (
                  <Button
                    variant="outline"
                    size="md"
                    onClick={() => {
                      document.body.style.overflow = 'hidden';
                      setIsAssignProfilesOpen(true);
                    }}
                  >
                    {t('pages.profile_assignment.team_action')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <PageHeader to={basePath + '?tab=teams'} ariaLabel={'placeholder'}>
            <div className="flex w-full items-center">
              <div className="flex items-center">
                <PersonAvatar
                  inline
                  id={teamInfo?.id}
                  name={teamInfo?.teamName}
                  bgColor={teamInfo?.color ? teamInfo?.color : null}
                ></PersonAvatar>
              </div>
              <div className="mx-4 flex w-full flex-row items-center justify-between gap-4">
                <h1 className="break-all text-lg font-semibold text-black sm:text-xl">
                  {teamInfo?.teamName}
                </h1>

                <div className="flex flex-wrap items-center gap-3">
                  {canAssignProfiles && (
                    <Button
                      variant="outline"
                      size="md"
                      onClick={() => {
                        document.body.style.overflow = 'hidden';
                        setIsAssignProfilesOpen(true);
                      }}
                    >
                      {t('pages.profile_assignment.team_action')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </PageHeader>
        )}
      </div>
      <div className="flex w-full flex-col sm:flex-row sm:items-center">
        <div className="pb-4 pt-5 sm:w-1/2">
          <InputText
            value={profileInpFilter}
            onChange={(ev) => setProfileInpFilter(ev.target.value)}
            Icon={Search}
            placeholder={t('pages.follow_up_users.search_profile')}
          />
        </div>
        {selectedRow?.size > 0 && (
          <div className="mb-4 ml-auto flex w-auto justify-end sm:mb-0">
            <Button
              onClick={() => {
                setIsOpenDelModal(true);
              }}
              size="sm"
              disabled={selectedRow?.size < 1}
              variant="outline"
              className="!text-error-04"
            >
              {t('pages.manage_competencies.delete_button')}
            </Button>
          </div>
        )}
      </div>

      <>
        {teamProfilesResult?.isLoading ? (
          <div className="rounded-lg border border-grey-02 px-5 py-4 text-sm text-[#7D7D81]">
            {t('pages.follow_up_users.profiles_loading')}
          </div>
        ) : teamProfiles.length === 0 ? (
          <EmptyTable
            template={1}
            text={t('empty_tenplate.profile_list')}
            btn_text={t('pages.profile_assignment.team_action')}
            btnClick={() => {
              document.body.style.overflow = 'hidden';
              setIsAssignProfilesOpen(true);
            }}
          />
        ) : (
          <div className="flex flex-col gap-3">
            {teamProfiles?.map((profile: any) => {
              const profileKey =
                profile.id || `${profile.userId}-${profile.professionId}`;
              const isExpanded = expandedProfileIds.has(profileKey);
              return (
                <div
                  key={profileKey}
                  className="overflow-hidden rounded-lg border border-grey-02 bg-[#F6F6F7]"
                >
                  <button
                    type="button"
                    className="grid w-full grid-cols-[28px_1fr_80px] items-center gap-3 bg-[#F6F6F7] px-4 py-4 text-left md:grid-cols-[28px_1fr_80px]"
                    onClick={() => {
                      setExpandedProfileIds((current) => {
                        const next = new Set(current);

                        if (next.has(profileKey)) {
                          next.delete(profileKey);
                        } else {
                          next.add(profileKey);
                        }

                        return next;
                      });
                    }}
                  >
                    <span
                      className="min-w-0"
                      onClick={(ev) => {
                        ev?.preventDefault?.();
                        ev?.stopPropagation?.();
                      }}
                    >
                      <Checkbox
                        id="selectedRow"
                        ariaLabel="test"
                        // onChange={() => console.log(profile.professionId)}
                        onChange={() => handleItemSelect(profile.professionId)}
                        checked={selectedRow.has(profile.professionId)}
                        key={
                          profile.professionId +
                          '_' +
                          selectedRow.has(profile.professionId)
                        }
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-base font-semibold text-[#231F20]">
                          {profile.professionName}
                        </span>
                      </span>
                      <span className="mt-1 text-xs text-[#7D7D81]">
                        {t('pages.profile_assignment.team_members_receive', {
                          count: teamInfo?.nrOfTeamMembers || 0,
                        })}
                      </span>
                    </span>
                    <div className="flex h-7 min-w-0 flex-row items-center justify-end gap-4 text-[#7D7D81]">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="!px-2 !py-2"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          downloadReport(profile.professionId);
                        }}
                      >
                        <Download className="h-5 w-5" />
                      </Button>

                      <ChevronRight
                        className={`h-5 w-5 transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                          isExpanded ? 'rotate-90' : 'rotate-0'
                        }`}
                      />
                    </div>
                  </button>

                  <div
                    className={`grid transition-[grid-template-rows] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div
                        className={`transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                          isExpanded
                            ? 'translate-y-0 opacity-100'
                            : '-translate-y-2 opacity-0'
                        }`}
                      >
                        <TeamProfessionCompetenceMatrix
                          professionId={profile.professionId}
                          teamId={teamId}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>

      {isAssignProfilesOpen && organisationId && teamId && (
        <AssignProfiles
          isOpen={isAssignProfilesOpen}
          onClose={() => {
            document.body.style.overflow = 'unset';
            setIsAssignProfilesOpen(false);
          }}
          onSaved={() => teamProfilesResult.refetch()}
          organisationId={organisationId}
          targetId={teamId}
          targetName={teamInfo?.teamName || ''}
          targetType="team"
        />
      )}

      {isOpenDelModal && (
        <ComponentDeleteModal
          closeModal={() => setIsOpenDelModal(false)}
          setIsOpenDeleteModal={setIsOpenDelModal}
          title={t(
            selectedRow?.size > 1
              ? 'delete_modal.team_profiles.title_m'
              : 'delete_modal.team_profiles.title'
          )}
          heading={t(
            selectedRow?.size > 1
              ? 'delete_modal.team_profiles.heading_m'
              : 'delete_modal.team_profiles.heading'
          )}
          text={t(
            selectedRow?.size > 1
              ? 'delete_modal.team_profiles.text_m'
              : 'delete_modal.team_profiles.text'
          )}
          handleDelete={() => deleteCompetencyProfile()}
        />
      )}
    </div>
  );
}

export default FollowUpTeamCompetencies;
